/**
 * Shared CAMERA-ANGLE ADJUSTER SHELL (_GLOBAL.md G7,
 * mockups/camera-angle-adjust.png).
 *
 * FROZEN SHARED COMPONENT — owned by the foundation-integrator. The
 * standalone Camera Lab panel was DROPPED (G8); this adjuster is now a
 * per-STORYBOARD-FRAME tool. The Storyboard agent OWNS the per-frame use
 * (wiring the frame image + persisting the chosen angle + the re-render
 * hook). The integrator owns this reusable shell.
 *
 * What the shell provides (matching the mockup):
 *   - a preview pane (the frame image),
 *   - a "Camera Position" orbit sphere: drag to orbit (pan/tilt),
 *     scroll / drag-up-down to dolly in/out,
 *   - a "CURRENT SHOT" descriptor (e.g. "Wide • Above • Slight Right"),
 *   - PAN / TILT / DOLLY numeric read-outs,
 *   - a CINEMATIC PRESETS row (3/4 Right, 3/4 Left, Top Down, Low Angle,
 *     Side Profile, Back Right).
 *
 * It emits the chosen `CameraAngle` via `onChange` (live) and `onApply`
 * (commit). The Storyboard agent maps that angle onto its frame and runs
 * its own re-generation.
 *
 *   import { openCameraAngleAdjuster } from "../shared";
 *
 *   adjustButton.addEventListener("click", () => {
 *     openCameraAngleAdjuster({
 *       image: frame.image,
 *       angle: frame.cameraAngle,
 *       onApply: (angle) => regenerateFrameAtAngle(frame, angle),
 *     });
 *   });
 */

import "./shared.css";
import { openModal } from "./Modal";

export interface CameraAngle {
  /** Horizontal orbit, degrees. Negative = left, positive = right. */
  pan: number;
  /** Vertical orbit, degrees. Negative = down, positive = up. */
  tilt: number;
  /** Camera distance, meters. Larger = further (wider). */
  dolly: number;
}

export interface CameraPreset {
  id: string;
  label: string;
  description: string;
  angle: CameraAngle;
}

export interface CameraAngleAdjusterOptions {
  /** Frame/preview image (data URL / url). */
  image?: string | null;
  /** Starting angle (partial; missing axes default). */
  angle?: Partial<CameraAngle>;
  /** Live updates as the user orbits / scrubs / picks a preset. */
  onChange?: (angle: CameraAngle) => void;
  /** Commit (Apply). */
  onApply?: (angle: CameraAngle) => void;
  /** Override the cinematic presets row. */
  presets?: CameraPreset[];
}

const DEFAULT_ANGLE: CameraAngle = { pan: 0, tilt: 0, dolly: 8 };

export const DEFAULT_CAMERA_PRESETS: CameraPreset[] = [
  { id: "three-quarter-right", label: "3/4 Right", description: "Camera positioned to the right front", angle: { pan: 45, tilt: 10, dolly: 8 } },
  { id: "three-quarter-left", label: "3/4 Left", description: "Camera positioned to the left front", angle: { pan: -45, tilt: 10, dolly: 8 } },
  { id: "top-down", label: "Top Down", description: "Camera directly above", angle: { pan: 0, tilt: 85, dolly: 7 } },
  { id: "low-angle", label: "Low Angle", description: "Camera below looking up", angle: { pan: 0, tilt: -40, dolly: 6 } },
  { id: "side-profile", label: "Side Profile", description: "Camera at 90° to the side", angle: { pan: 90, tilt: 0, dolly: 7 } },
  { id: "back-right", label: "Back Right", description: "Camera behind and to the right", angle: { pan: 135, tilt: 10, dolly: 9 } },
];

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

/** Build a human "Wide • Above • Slight Right"-style shot descriptor. */
export function describeShot(angle: CameraAngle): string {
  const distance = angle.dolly >= 9 ? "Wide" : angle.dolly <= 4 ? "Close" : "Medium";
  const height =
    angle.tilt >= 25 ? "Above" : angle.tilt <= -25 ? "Below" : "Eye Level";
  const side =
    angle.pan === 0
      ? "Front"
      : `${Math.abs(angle.pan) >= 75 ? "Hard" : Math.abs(angle.pan) >= 30 ? "Slight" : "Subtle"} ${angle.pan > 0 ? "Right" : "Left"}`;
  return `${distance} • ${height} • ${side}`;
}

export class CameraAngleAdjuster {
  private readonly root: HTMLDivElement;
  private readonly previewImg: HTMLImageElement;
  private readonly shotLabel: HTMLDivElement;
  private readonly sphereDot: HTMLDivElement;
  private readonly panValue: HTMLSpanElement;
  private readonly tiltValue: HTMLSpanElement;
  private readonly dollyValue: HTMLSpanElement;
  private readonly onChange?: (angle: CameraAngle) => void;
  private readonly onApplyHook?: (angle: CameraAngle) => void;

  private angle: CameraAngle;
  private dragging = false;

  constructor(options: CameraAngleAdjusterOptions = {}) {
    this.onChange = options.onChange;
    this.onApplyHook = options.onApply;
    this.angle = { ...DEFAULT_ANGLE, ...options.angle };

    this.root = document.createElement("div");
    this.root.className = "studio-cam-adjuster";

    // ---- Top row: preview + camera-position sphere -----------------
    const top = document.createElement("div");
    top.className = "studio-cam-top";

    const previewPane = document.createElement("div");
    previewPane.className = "studio-cam-preview";
    this.previewImg = document.createElement("img");
    this.previewImg.className = "studio-cam-preview-img";
    this.previewImg.alt = "Shot preview";
    if (options.image) this.previewImg.src = options.image;
    previewPane.appendChild(this.previewImg);

    const positionPane = document.createElement("div");
    positionPane.className = "studio-cam-position";
    const posHeader = document.createElement("div");
    posHeader.className = "studio-cam-pos-header";
    posHeader.innerHTML =
      '<span class="studio-cam-pos-title">📷 Camera Position</span><span class="studio-cam-ready">READY</span>';
    const posHint = document.createElement("div");
    posHint.className = "studio-cam-pos-hint";
    posHint.textContent = "Drag sphere to orbit • Scroll to dolly in/out";

    const shotCard = document.createElement("div");
    shotCard.className = "studio-cam-shot-card";
    const shotCaption = document.createElement("div");
    shotCaption.className = "studio-cam-shot-caption";
    shotCaption.textContent = "CURRENT SHOT";
    this.shotLabel = document.createElement("div");
    this.shotLabel.className = "studio-cam-shot-label";
    shotCard.appendChild(shotCaption);
    shotCard.appendChild(this.shotLabel);

    const sphere = document.createElement("div");
    sphere.className = "studio-cam-sphere";
    sphere.dataset.kokoAction = "camera-sphere";
    for (const dir of ["FRONT", "BACK", "LEFT", "RIGHT"]) {
      const cardinal = document.createElement("span");
      cardinal.className = `studio-cam-cardinal is-${dir.toLowerCase()}`;
      cardinal.textContent = dir;
      sphere.appendChild(cardinal);
    }
    this.sphereDot = document.createElement("div");
    this.sphereDot.className = "studio-cam-dot";
    sphere.appendChild(this.sphereDot);
    this.wireSphere(sphere);

    positionPane.appendChild(posHeader);
    positionPane.appendChild(posHint);
    positionPane.appendChild(shotCard);
    positionPane.appendChild(sphere);

    top.appendChild(previewPane);
    top.appendChild(positionPane);

    // ---- Read-out row: PAN / TILT / DOLLY --------------------------
    const readouts = document.createElement("div");
    readouts.className = "studio-cam-readouts";
    const pan = this.buildReadout("PAN");
    const tilt = this.buildReadout("TILT");
    const dolly = this.buildReadout("DOLLY");
    this.panValue = pan.value;
    this.tiltValue = tilt.value;
    this.dollyValue = dolly.value;
    readouts.appendChild(pan.box);
    readouts.appendChild(tilt.box);
    readouts.appendChild(dolly.box);

    // ---- Presets ---------------------------------------------------
    const presetsLabel = document.createElement("div");
    presetsLabel.className = "studio-cam-presets-label";
    presetsLabel.textContent = "CINEMATIC PRESETS";
    const presetsRow = document.createElement("div");
    presetsRow.className = "studio-cam-presets";
    for (const preset of options.presets ?? DEFAULT_CAMERA_PRESETS) {
      presetsRow.appendChild(this.buildPresetButton(preset));
    }

    // ---- Apply -----------------------------------------------------
    const actions = document.createElement("div");
    actions.className = "studio-cam-actions";
    const applyButton = document.createElement("button");
    applyButton.type = "button";
    applyButton.className = "studio-button is-primary";
    applyButton.textContent = "Apply angle";
    applyButton.dataset.kokoAction = "camera-apply";
    applyButton.addEventListener("click", () => this.onApplyHook?.(this.getAngle()));
    actions.appendChild(applyButton);

    this.root.appendChild(top);
    this.root.appendChild(readouts);
    this.root.appendChild(presetsLabel);
    this.root.appendChild(presetsRow);
    this.root.appendChild(actions);

    this.render();
  }

  getElement(): HTMLElement {
    return this.root;
  }

  getAngle(): CameraAngle {
    return { ...this.angle };
  }

  setAngle(patch: Partial<CameraAngle>): void {
    this.angle = {
      pan: clamp(patch.pan ?? this.angle.pan, -180, 180),
      tilt: clamp(patch.tilt ?? this.angle.tilt, -90, 90),
      dolly: clamp(patch.dolly ?? this.angle.dolly, 1, 20),
    };
    this.render();
    this.onChange?.(this.getAngle());
  }

  destroy(): void {
    this.root.remove();
  }

  // ------------------------------------------------------------------

  private buildReadout(
    name: string,
  ): { box: HTMLDivElement; value: HTMLSpanElement } {
    const box = document.createElement("div");
    box.className = "studio-cam-readout";
    const caption = document.createElement("div");
    caption.className = "studio-cam-readout-name";
    caption.textContent = name;
    const value = document.createElement("span");
    value.className = "studio-cam-readout-value";
    const sub = document.createElement("div");
    sub.className = "studio-cam-readout-sub";
    sub.dataset.for = name.toLowerCase();
    box.appendChild(caption);
    box.appendChild(value);
    box.appendChild(sub);
    return { box, value };
  }

  private buildPresetButton(preset: CameraPreset): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-cam-preset";
    button.dataset.preset = preset.id;
    button.dataset.kokoAction = "camera-preset";
    const label = document.createElement("div");
    label.className = "studio-cam-preset-label";
    label.textContent = preset.label;
    const desc = document.createElement("div");
    desc.className = "studio-cam-preset-desc";
    desc.textContent = preset.description;
    button.appendChild(label);
    button.appendChild(desc);
    button.addEventListener("click", () => this.setAngle(preset.angle));
    return button;
  }

  private wireSphere(sphere: HTMLDivElement): void {
    let lastX = 0;
    let lastY = 0;
    const onMove = (event: PointerEvent): void => {
      if (!this.dragging) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      this.setAngle({
        pan: this.angle.pan + dx * 0.6,
        tilt: this.angle.tilt - dy * 0.6,
      });
    };
    sphere.addEventListener("pointerdown", (event) => {
      this.dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      sphere.setPointerCapture(event.pointerId);
    });
    sphere.addEventListener("pointermove", onMove);
    const stop = (event: PointerEvent): void => {
      this.dragging = false;
      try {
        sphere.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }
    };
    sphere.addEventListener("pointerup", stop);
    sphere.addEventListener("pointerleave", stop);
    sphere.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        this.setAngle({ dolly: this.angle.dolly + Math.sign(event.deltaY) * 0.5 });
      },
      { passive: false },
    );
  }

  private render(): void {
    this.shotLabel.textContent = describeShot(this.angle);
    this.panValue.textContent = `${Math.round(this.angle.pan)}°`;
    this.tiltValue.textContent = `${Math.round(this.angle.tilt)}°`;
    this.dollyValue.textContent = `${this.angle.dolly.toFixed(1)}m`;

    this.setReadoutSub("pan", this.angle.pan === 0 ? "Center" : this.angle.pan > 0 ? "Right" : "Left");
    this.setReadoutSub("tilt", this.angle.tilt === 0 ? "Level" : this.angle.tilt > 0 ? "Up" : "Down");
    this.setReadoutSub("dolly", this.angle.dolly >= 9 ? "Far" : this.angle.dolly <= 4 ? "Close" : "Mid");

    // Place the sphere dot: pan → x (around equator), tilt → y.
    const x = 50 + (this.angle.pan / 180) * 42;
    const y = 50 - (this.angle.tilt / 90) * 42;
    this.sphereDot.style.left = `${clamp(x, 6, 94)}%`;
    this.sphereDot.style.top = `${clamp(y, 6, 94)}%`;
  }

  private setReadoutSub(axis: string, text: string): void {
    const el = this.root.querySelector<HTMLDivElement>(
      `.studio-cam-readout-sub[data-for="${axis}"]`,
    );
    if (el) el.textContent = text;
  }
}

export interface OpenCameraAngleAdjusterOptions extends CameraAngleAdjusterOptions {
  /** Modal title; defaults to "Adjust Camera Angle". */
  title?: string;
}

/** Open the shared camera-angle adjuster in a modal. Returns a close fn. */
export function openCameraAngleAdjuster(
  options: OpenCameraAngleAdjusterOptions = {},
): () => void {
  const adjuster = new CameraAngleAdjuster(options);
  const modal = openModal({
    title: options.title ?? "Adjust Camera Angle",
    variant: "is-camera-adjuster",
    surface: "camera-adjuster",
    onClose: () => adjuster.destroy(),
  });
  modal.body.appendChild(adjuster.getElement());
  return modal.close;
}
